import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/pro-solid-svg-icons";
import {
  ConfirmationActionButtonWrapper,
  ConfirmationBoldLine,
  ConfirmationContent,
  ConfirmationErrorText,
  ConfirmationFooter,
  ConfirmationFooterButton,
  ConfirmationFooterSpinner,
  ConfirmationMessage,
  ConfirmationSearchTable,
  ConfirmationSearchTableRow,
  SearchTableCell,
} from "./GlobalSearchModal.styles";
import { formatWithTimezoneOffset } from "../../utils/helpers";

export type AffiliationConfirmationMode = "affiliate" | "disaffiliate";

export interface AffiliationConfirmationPatient {
  first_name?: string | null;
  last_name?: string | null;
  date_of_birth?: string | null;
  due_date?: string | null;
  city?: string | null;
  state?: string | null;
  physician_name?: string | null;
}

const formatDate = (iso: string | null | undefined): string =>
  formatWithTimezoneOffset(iso ?? undefined) || "—";

export const AffiliationConfirmationContent: React.FC<{
  mode: AffiliationConfirmationMode;
  patient: AffiliationConfirmationPatient;
  accountName?: string;
  isSubmitting: boolean;
  actionsDisabled?: boolean;
  error: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({
  mode,
  patient,
  accountName,
  isSubmitting,
  actionsDisabled = false,
  error,
  onConfirm,
  onCancel,
}) => {
  const isDisaffiliate = mode === "disaffiliate";
  const buttonsDisabled = isSubmitting || actionsDisabled;

  return (
    <ConfirmationContent>
      {isDisaffiliate ? (
        <ConfirmationMessage>
          If you proceed, you will not be informed of new test results or be
          able to order additional tests for this patient.
          <ConfirmationBoldLine>
            Are you sure you want to remove this patient from your list?
          </ConfirmationBoldLine>
        </ConfirmationMessage>
      ) : (
        <>
          If you proceed, you will have visibility to the patient&apos;s medical
          information and other PHI.
          <ConfirmationBoldLine>
            {`Are you sure you want to add this patient to your list${
              accountName ? ` for ${accountName}` : ""
            }?`}
          </ConfirmationBoldLine>
        </>
      )}
      <ConfirmationSearchTable>
        <tbody>
          <ConfirmationSearchTableRow>
            <SearchTableCell>
              {patient.first_name} {patient.last_name}
            </SearchTableCell>
            <SearchTableCell>
              {formatDate(patient.date_of_birth)}
            </SearchTableCell>
            <SearchTableCell>{formatDate(patient.due_date)}</SearchTableCell>
            <SearchTableCell>
              {[patient.city, patient.state].filter(Boolean).join(", ") || "—"}
            </SearchTableCell>
            <SearchTableCell>{patient.physician_name ?? "—"}</SearchTableCell>
          </ConfirmationSearchTableRow>
        </tbody>
      </ConfirmationSearchTable>
      <ConfirmationFooter>
        <ConfirmationFooterButton
          onClick={onCancel}
          disabled={buttonsDisabled}
          $isSubmitting={isSubmitting}
        >
          CANCEL
        </ConfirmationFooterButton>
        <ConfirmationActionButtonWrapper>
          <ConfirmationFooterButton
            onClick={onConfirm}
            disabled={buttonsDisabled}
            $isSubmitting={isSubmitting}
          >
            {isDisaffiliate ? "REMOVE PATIENT" : "ADD PATIENT"}
          </ConfirmationFooterButton>
          {isSubmitting && (
            <ConfirmationFooterSpinner>
              <FontAwesomeIcon icon={faSpinner} spin />
            </ConfirmationFooterSpinner>
          )}
        </ConfirmationActionButtonWrapper>
        {error && (
          <ConfirmationErrorText>
            There was a network error. Please try again.
          </ConfirmationErrorText>
        )}
      </ConfirmationFooter>
    </ConfirmationContent>
  );
};
